import { useMemo } from 'react';

import type { ChartData, ChartFields } from '../../types';
import { Plot } from '../Plot';
import { Empty } from '../states';
import { buildFrame, frameNote, isEmpty } from '../frame';
import { compact, exact } from '../format';

/**
 * Top-N rankings.
 *
 * A ranked list beats a bar chart when the reader's question is "who is on top?"
 * rather than "how much bigger?" — the ordering does the work and the names stay
 * left-aligned and readable at any length.
 *
 * Each row still carries its magnitude as a track behind the name, so the shape
 * of the distribution (runaway leader vs a tight pack) is visible without
 * reading every number. One series, so one colour: the track is not encoding
 * identity, only size.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

const MAX_ROWS = 50;

export const Leaderboard = ({ data, fields }: Props) => {
  const view = useMemo(() => {
    const frame = buildFrame(data, { limit: MAX_ROWS, ...fields });
    if (isEmpty(frame)) return null;

    const values = frame.series[0].values;
    const max = Math.max(...values.map((v) => v ?? 0), 0) || 1;

    return {
      note: frameNote(frame),
      rows: frame.categories.map((name, i) => ({
        name,
        value: values[i],
        share: Math.max(0, (values[i] ?? 0) / max),
      })),
    };
  }, [data]);

  if (!view) return <Empty />;

  return (
    <Plot note={view.note}>
      <ol className="leaderboard">
        {view.rows.map((row, i) => (
        <li className="leaderboard__row" key={`${row.name}-${i}`}>
          <span className="leaderboard__rank">{i + 1}</span>
          <span className="leaderboard__body">
            <span className="leaderboard__name" title={row.name}>
              {row.name}
            </span>
            <span className="leaderboard__meter" aria-hidden="true">
              <span
                className="leaderboard__fill"
                style={{ width: `${(row.share * 100).toFixed(2)}%` }}
              />
            </span>
          </span>
          <span className="leaderboard__value" title={row.value === null ? '' : exact(row.value)}>
            {row.value === null ? '—' : compact(row.value)}
          </span>
        </li>
      ))}
      </ol>
    </Plot>
  );
};
